<template>
  <div class="range-row-container mb-3">
    <v-select
      :label="label"
      :items="dateRangeList"
      :value="selectedRange"
      outlined
      hide-details
      item-text="name"
      :menu-props="dateRangeMenuProps"
      class="date-range"
    >
      <template #append>
        <arrow-icon class="date-range-arrow-down-icon" />
      </template>

      <template #selection="{ item }">
        <span class="selected-range">
          {{ $t(`affiliateProgram.links.${item.value}`) }}
        </span>
      </template>

      <template #item="{ item }">
        <v-list-item @click="changeSelectedRange(item)">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t(`affiliateProgram.links.${item.value}`) }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>

    <!--  SPECIFIC DATE  -->
    <date-calendar-field
      v-if="isRange('specificDate')"
      :value="selectedRange.dateFrom"
      :label="$t('affiliateProgram.links.chooseDate')"
      @change="handleCurrentDate"
    />

    <!--  RANGE  -->
    <div
      v-if="isRange('range')"
      class="d-flex align-center"
    >
      <date-calendar-field
        :value="selectedRange.dateFrom"
        :label="$t('affiliateProgram.links.dateFrom')"
        @change="handleRangeDate('dateFrom', $event)"
      />

      <dash-icon class="date-range-dash-icon" />

      <date-calendar-field
        :value="selectedRange.dateTo"
        :label="$t('affiliateProgram.links.dateTo')"
        @change="handleRangeDate('dateTo', $event)"
      />
    </div>
  </div>
</template>

<script>
import ArrowIcon from '@/assets/svgIcons/arrowDownIcon.svg?inline'
import DashIcon from '@/assets/svgIcons/dashIcon.svg?inline'
import DateCalendarField from '@/components/dateRanges/DateCalendarField.vue'

export default {
  name: 'DateRange',

  components: {
    DateCalendarField,
    ArrowIcon,
    DashIcon
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    selectedRange: {
      required: true
    }
  },

  computed: {
    dateRangeMenuProps () {
      return ({
        maxWidth: '300px',
        rounded: 'lg',
        offsetY: true,
        closeOnClick: true,
        contentClass: 'date-range-menu',
        closeOnContentClick: true
      })
    },
    dateRangeList () {
      const today = new Date()
      const todayISOString = today.toISOString().substring(0, 10)

      const yesterday = new Date(today)
      yesterday.setDate(today.getDate() - 1)
      const yesterdayISOString = yesterday.toISOString().substring(0, 10)

      const lastWeek = new Date()
      lastWeek.setDate(today.getDate() - 7)
      const lastWeekISOString = lastWeek.toISOString().substring(0, 10)

      const lastMonth = new Date()
      lastMonth.setMonth(today.getMonth() - 1)
      const lastMonthISOString = lastMonth.toISOString().substring(0, 10)

      return [
        {
          value: 'today',
          dateFrom: todayISOString,
          dateTo: todayISOString
        },
        {
          value: 'yesterday',
          dateFrom: yesterdayISOString,
          dateTo: yesterdayISOString
        },
        {
          value: 'inWeek',
          dateFrom: lastWeekISOString,
          dateTo: todayISOString
        },
        {
          value: 'inMonth',
          dateFrom: lastMonthISOString,
          dateTo: todayISOString
        },
        {
          value: 'specificDate',
          dateFrom: this.selectedRange?.dateFrom,
          dateTo: this.selectedRange?.dateFrom
        },
        {
          value: 'range',
          dateFrom: '',
          dateTo: ''
        },
        {
          value: 'forAllTheTime',
          dateFrom: '',
          dateTo: ''
        }
      ]
    },
  },

  methods: {
    handleCurrentDate (value) {
      const rangeData = {
        ...this.selectedRange,
        dateFrom: value,
        dateTo: value
      }

      this.changeSelectedRange(rangeData)
    },

    handleRangeDate (key, value) {
      const rangeData = {
        ...this.selectedRange,
        [key]: value
      }

      this.changeSelectedRange(rangeData)
    },

    changeSelectedRange (value) {
      this.$emit('handleSelected', value)
    },

    isRange (value) {
      return this.selectedRange?.value === value
    }
  }
}
</script>

<style lang="scss">
.range-row-container {
  display: flex;
  column-gap: 8px;
}

.v-application--is-ltr .v-text-field.date-range .v-input__append-inner {
  padding: 0;
  margin: auto 0 auto 8px;
  cursor: pointer;
}

.date-range {
  max-width: 170px;
  width: 170px;
  height: max-content;

  .selected-range {
    color: var(--v-input-foreground-typed-base);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  &.v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: 40px;
    height: 40px;
    cursor: auto;
  }

  &.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot {
    padding: 0 16px;
  }

  .v-input__append-inner svg {
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), visibility 0s;
    margin: auto 0;
    padding: 0;
  }

  &.v-select--is-menu-active {
    .v-input__append-inner svg {
      transform: rotate(180deg);
    }
  }

  .v-input__slot {
    background: var(--v-input-background-default-base);
  }

  &.v-text-field--outlined fieldset,
  &.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state):not(.v-input--is-disabled) > .v-input__control > .v-input__slot:hover fieldset {
    border-radius: 8px;
    border: 1px solid var(--v-input-border-default-base);
    color: transparent;
  }

  &.v-text-field--outlined.v-select--is-menu-active:not(.v-input--has-state):not(.v-input--is-disabled) > .v-input__control > .v-input__slot fieldset {
    border: 1px solid var(--v-input-border-focused-base);
  }

  .date-range-arrow-down-icon {
    width: 20px;
    height: 20px;
    color: var(--v-icon-tertiary-base);
  }
}
.date-range-menu {
  .v-list-item {
    height: 36px;
    min-height: unset;
    padding: 6px 16px;
    flex: unset;

    .v-list-item__title {
      flex: unset;
      margin-bottom: 0;
      color: var(--v-text-primary-base);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }

  &::-webkit-scrollbar {
    border-radius: 0 8px 8px 0;
  }

  &::-webkit-scrollbar,
  &::-webkit-scrollbar-thumb {
    background-color: var(--v-con-tertiary-base);
  }
}

.date-range-dash-icon {
  width: 12px;
  height: 12px;
  margin: 0 8px;
  color: var(--v-text-primary-base);
}
</style>
